import React from 'react';
import {
  Box,
  Grid,
  IconButton,
  SxProps,
  TextField,
  Typography,
} from '@mui/material';
import {
  HistoryIcon,
  AddIcon,
  SendIcon,
  WorkSpaceIcon,
  ConnectionsIcon,
} from 'components/SVG';
import { FileRejection } from 'react-dropzone';
import { AttachmentCardType } from './AttachmentCard';
import { Workspace } from 'types/Workspace';
import { Memory } from 'types/Memory';
import { usePopover } from 'hooks/common';
import { SparkImageLoader } from 'sharedComponents/UI';
import Globe from 'components/SVG/Globe';
import AddIconMenu from './ChatBubblePopovers/AddIconMenu';
import MemoryBulbIcon from 'components/SVG/MemoryBulbIcon';
import PluginsPopover from './ChatBubblePopovers/PluginsPopover';

const IconWrapper = ({
  children,
  containerStyles,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: (
    event: HTMLElement | React.MouseEvent<HTMLElement, MouseEvent>
  ) => void;
  containerStyles?: SxProps;
  disabled?: boolean;
}) => {
  return (
    <IconButton onClick={onClick} disabled={disabled} sx={{ padding: '0px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid lightgray',
          borderRadius: '20px',
          padding: '3px',
          ...containerStyles,
        }}
      >
        {children}
      </Box>
    </IconButton>
  );
};

type ChatPromptInputFieldProps = {
  openDialog?: () => void;
  onSendMessage: (value: string) => void;
  expandable?: boolean;
  disabled?: boolean;
  handleFileUpload: (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => Promise<(string | undefined)[] | undefined>;
  onAddAttachments: (attachments: AttachmentCardType) => void;
  isUploadingFiles?: boolean;
  chatID?: string;
};

const ChatPromptInputField = ({
  onSendMessage,
  openDialog,
  disabled = false,
  expandable = true,
  handleFileUpload,
  onAddAttachments,
  isUploadingFiles = false,
  chatID,
}: ChatPromptInputFieldProps) => {
  const [value, setValue] = React.useState('');
  const {
    anchorEl: addIconAnchorEl,
    open: addIconOpen,
    closePopover: closeAddIconPopover,
    openPopover: openAddIconPopover,
  } = usePopover();
  const {
    anchorEl: pluginsAnchorEl,
    open: pluginsOpen,
    closePopover: closePluginsPopover,
    openPopover: openPluginsPopover,
  } = usePopover();

  React.useEffect(() => {
    if (isUploadingFiles) {
      closeAddIconPopover();
    }
  }, [closeAddIconPopover, isUploadingFiles]);

  const handleSendMessage = (val: string) => {
    setValue('');
    onSendMessage(val);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage(value);
      return;
    }
    if (event.key === 'Backspace') {
      event.stopPropagation();
    }
  };

  const handleAddWorkspaceAttachment = (workspace: Workspace) => {
    const attachment: AttachmentCardType = {
      id: workspace.id,
      title: workspace.attributes.name,
      cardType: 'workspace',
      thumbnail: workspace.attributes.imageURL || '',
      actionKey: workspace.id,
      fileTypeTag: '',
      file: null,
      details: 'Workspace',
      isUploading: false,

      iconComponent: (
        <Box sx={{ display: 'flex' }}>
          {workspace?.attributes.imageURL ? (
            <SparkImageLoader
              src={workspace?.attributes.imageURL}
              alt={workspace?.attributes.imageURL}
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '10px',
              }}
            />
          ) : (
            <WorkSpaceIcon width='34' height='34' />
          )}
        </Box>
      ),
    };
    onAddAttachments?.(attachment);
    closeAddIconPopover();
  };

  const handleAddKnowledgeAttachment = (knowledge: Memory) => {
    const attachment: AttachmentCardType = {
      id: knowledge.id,
      title: knowledge.attributes.name,
      cardType: 'memory',
      thumbnail: knowledge.attributes.iconURL || '',
      actionKey: knowledge.id,
      fileTypeTag: '',
      file: null,
      details: 'Source',
      isUploading: false,
      iconComponent: !knowledge.attributes.iconURL ? (
        <MemoryBulbIcon
          stroke='var( --color-primary)'
          width={30}
          height={30}
        ></MemoryBulbIcon>
      ) : null,
    };
    onAddAttachments?.(attachment);
    closeAddIconPopover();
  };

  const handlePasteFromClipboard = (
    e: React.ClipboardEvent<HTMLDivElement>
  ) => {
    const items = e.clipboardData && Array.from(e.clipboardData.items);

    const imageItem = items?.find((item) => item.type.startsWith('image/'));
    if (imageItem) {
      const file = imageItem.getAsFile();
      if (file) {
        e.preventDefault();
        handleFileUpload([file], []);
      }
    }
  };

  return (
    <Grid
      item
      container
      position='relative'
      paddingTop={2}
      onClick={(e) => e.stopPropagation()}
    >
      <TextField
        multiline={true}
        onKeyDown={handleKeyDown}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        onPaste={handlePasteFromClipboard}
        disabled={disabled}
        sx={{
          '& .MuiInputBase-fullWidth': { padding: 0 },
          '& fieldset': { outline: 'none', border: 'none' },
          '& textarea': { py: 0, px: 0, pl: 1 },
        }}
        placeholder='Ask anything'
        fullWidth
        autoComplete='off'
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        width='100%'
        paddingY={1}
      >
        <Box
          sx={{
            position: 'relative',
            zIndex: 10,
            display: 'flex',
            gap: 1,
            paddingLeft: 1,
          }}
        >
          <IconWrapper
            onClick={openAddIconPopover}
            disabled={disabled}
            containerStyles={{
              bgcolor: addIconOpen ? '#ECEFFE' : 'white',
              borderColor: addIconOpen ? '#6267AC' : 'lightgray',
            }}
          >
            <AddIcon
              width='24'
              height='24'
              stroke={disabled ? 'lightgray' : 'black'}
            />
          </IconWrapper>
          {expandable && (
            <IconWrapper
              onClick={openAddIconPopover}
              disabled={disabled}
              containerStyles={{ paddingX: 1.5 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Globe width='24' height='24' strokeWidth='1.75' />
                <Typography variant='body2' color='black'>
                  Search
                </Typography>
              </Box>
            </IconWrapper>
          )}
          <IconWrapper
            onClick={openPluginsPopover}
            disabled={disabled}
            containerStyles={{
              paddingX: 1.5,
              bgcolor: pluginsOpen ? '#ECEFFE' : 'white',
              borderColor: pluginsOpen ? '#6267AC' : 'lightgray',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <ConnectionsIcon width='24' height='24' stroke='black' />
              <Typography variant='body2' color='black'>
                Plugins
              </Typography>
            </Box>
          </IconWrapper>
        </Box>

        <Box display='flex' gap={1}>
          {expandable && (
            <IconWrapper onClick={() => openDialog?.()} disabled={disabled}>
              <HistoryIcon width='24' height='24' stroke='black' />
            </IconWrapper>
          )}

          <IconWrapper
            disabled={!value.trim().length || disabled}
            onClick={() => handleSendMessage(value)}
            containerStyles={{
              bgcolor:
                !value.trim().length || disabled ? 'lightgray' : '#6267AC',
              border: 'none',
            }}
          >
            <SendIcon width='24' height='24' color='white' />
          </IconWrapper>
        </Box>
      </Box>

      {addIconOpen && (
        <AddIconMenu
          onFileUpload={handleFileUpload}
          disabled={disabled}
          open={addIconOpen}
          anchorEl={addIconAnchorEl}
          onClose={closeAddIconPopover}
          onAddWorkspaceAttachment={handleAddWorkspaceAttachment}
          onAddKnowledgeAttachment={handleAddKnowledgeAttachment}
        />
      )}
      {pluginsOpen && (
        <PluginsPopover
          open={pluginsOpen}
          anchorEl={pluginsAnchorEl}
          onClose={closePluginsPopover}
          chatID={chatID || ''}
        />
      )}
    </Grid>
  );
};

export default ChatPromptInputField;
